import React, { RefObject } from 'react';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { View, StyleSheet, Text, FlatList } from 'react-native';

import { isWeb } from '@helpers';
import { font } from '@styles/fonts';
import ColorWrapper, { WithColor } from '@styles/colors';

import Bar from './components/bar';
import Header from './components/header';

import { Moment } from 'moment';
import { RenderChart } from '@components/ncFiles/components/paid';
import OutsidePressHandler from 'react-native-outside-press';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManagePaidBarChart } from './hooks/useManagePaidBarChart';

export type ValueName = 'totalProductiveSecs' | 'averageActiveSecs' | 'averageChangeSecs' | 'averageSetupSecs';

const BarChart = (props: WithColor & {
  selectedDate: string;
  padding: number;
  chartWidth: number;
  getData: ThunkAction<Promise<void>, any, unknown, Action<any>>;
  id: number;
  barAmount: number;
  renderChart: RenderChart[];
  selectedIndex: number;
  saveChartData: (date: Moment | string, data: any) => void;
  barValueName: ValueName;
  lineValueName: ValueName;
  lineText: string;
  barText: string;
  lineColor: string;
  barColor: string;
  average: number;
  averagePaid: number;
  quantity: number;
  batches: number;
  showAverage: boolean;
  userStopScroll: (number: number) => void;
  flatListRef: RefObject<FlatList<any>>;
  barBlockWidth: number;
  barWidth: number;
  idKey: string;
}) => {
  const {
    C,
    selectedDate,
    padding,
    chartWidth,
    getData,
    id,
    barAmount,
    renderChart,
    selectedIndex,
    saveChartData,
    barValueName,
    lineValueName,
    lineText,
    barText,
    lineColor,
    barColor,
    average = 0,
    averagePaid = 0,
    quantity,
    batches,
    showAverage,
    userStopScroll,
    flatListRef,
    idKey = 'ncFileId',
  } = props;

  const {
    item,
    chartHeight,
    rightLegendFontOffset,
    maxValue,
    barBlockWidth,
    barWidth,
    formatLegend,
    selectedBarIndex,
    setSelectedBarIndex,
    selectedBar,
    setSelectedBar,
  } = useManagePaidBarChart({
    renderChart,
    selectedIndex,
    chartWidth,
    barAmount,
    barValueName,
    lineValueName,
    average,
    averagePaid,
    showAverage,
  });

  return (
    <OutsidePressHandler
      onOutsidePress={() => {
        setSelectedBarIndex(null);
        setSelectedBar(null);
      }}
    >
      <View testID={TEST_IDS.paidBarChart.container} style={{ paddingHorizontal: padding, marginBottom: 25 }}>
        <Header
          {...{
            item: renderChart[selectedIndex],
            C,
            barValueName,
            lineValueName,
            lineText,
            barText,
            lineColor,
            barColor,
            quantity,
            batches,
            showAverage,
            selectedBarIndex,
            setSelectedBarIndex,
            selectedBar,
            setSelectedBar,
            barBlockWidth,
            chartHeight,
          }}
        />
        <View
          style={{
            width: '100%',
          }}
        >
          <View
            testID={TEST_IDS.paidBarChart.chartArea}
            style={{
              height: isWeb ? chartHeight : 136,
              width: chartWidth,
            }}
          >
            <View
              style={{
                height: StyleSheet.hairlineWidth,
                width: '100%',
                backgroundColor: C('Hairline/Light/Secondary'),
                position: 'absolute',
                top: 0,
              }}
            />
            <View
              style={{
                height: StyleSheet.hairlineWidth,
                width: '100%',
                backgroundColor: C('Hairline/Light/Secondary'),
                position: 'absolute',
                top: chartHeight,
              }}
            />
            <View
              style={{
                height: StyleSheet.hairlineWidth,
                width: '100%',
                backgroundColor: C('Hairline/Light/Secondary'),
                position: 'absolute',
                top: chartHeight / 2,
              }}
            />
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'flex-end',
                height: '100%',
              }}
            >
              <View style={{ zIndex: 1000 }}>
                {isWeb ? (
                  <Bar
                    {...{
                      item: item as RenderChart,
                      padding,
                      chartWidth,
                      chartHeight,
                      C,
                      getData,
                      id,
                      idKey,
                      barBlockWidth,
                      barWidth,
                      barAmount,
                      saveChartData,
                      selectedDate,
                      barValueName,
                      lineValueName,
                      lineColor,
                      barColor,
                      average,
                      averagePaid,
                      showAverage,
                      userStopScroll,
                      selectedBarIndex,
                      setSelectedBarIndex,
                      setSelectedBar,
                    }}
                  />
                ) : (
                  <FlatList
                    ref={flatListRef}
                    data={renderChart}
                    renderItem={({ item }) => (
                      <Bar
                        {...{
                          item,
                          padding,
                          chartWidth,
                          chartHeight,
                          C,
                          getData,
                          id,
                          idKey,
                          barBlockWidth,
                          barWidth,
                          barAmount,
                          saveChartData,
                          selectedDate,
                          barValueName,
                          lineValueName,
                          lineColor,
                          barColor,
                          showAverage,
                          userStopScroll,
                          average,
                          averagePaid,
                          selectedBarIndex,
                          setSelectedBar,
                          setSelectedBarIndex,
                        }}
                      />
                    )}
                    horizontal={true}
                    pagingEnabled={true}
                    initialScrollIndex={selectedIndex}
                    keyExtractor={(item) => item.date}
                    getItemLayout={(_, index) => ({
                      index,
                      length: chartWidth,
                      offset: index * chartWidth,
                    })}
                    maxToRenderPerBatch={2}
                    initialNumToRender={2}
                    windowSize={2}
                    inverted={true}
                    onMomentumScrollEnd={(e) => userStopScroll(e.nativeEvent.contentOffset.x)}
                    showsHorizontalScrollIndicator={false}
                    maintainVisibleContentPosition={{ minIndexForVisible: 0 }}
                  />
                )}
              </View>
            </View>
            <View
              testID={TEST_IDS.paidBarChart.rightLegend}
              style={{
                position: 'absolute',
                top: 0,
                right: -40,
                height: '100%',
                width: 40,
              }}
            >
              <View style={{ position: 'absolute', top: -rightLegendFontOffset, left: 6 }}>
                <Text style={[font['Footnote'], { color: C('Label/Light/Quaternary') }]}>{formatLegend(maxValue)}</Text>
              </View>
              <View style={{ position: 'absolute', top: chartHeight / 2 - rightLegendFontOffset, left: 6 }}>
                <Text style={[font['Footnote'], { color: C('Label/Light/Quaternary') }]}>{formatLegend(maxValue / 2)}</Text>
              </View>
              <View style={{ position: 'absolute', bottom: -rightLegendFontOffset, left: 6 }}>
                <Text style={[font['Footnote'], { color: C('Label/Light/Quaternary') }]}>0</Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </OutsidePressHandler>
  );
};

export default ColorWrapper(BarChart);
